'use client';

import React, { useState, useTransition, useId } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormState {
  status: 'idle' | 'success' | 'error';
  message: string;
}

interface FieldErrors {
  fullName?: string;
  phone?: string;
  pickupLocation?: string;
  destinationLocation?: string;
  transportDate?: string;
  transportTime?: string;
}

function isValidPhone(phone: string): boolean {
  return /^[\d\s\-().+]{7,20}$/.test(phone.trim());
}

function isValidDate(date: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(Date.parse(date));
}

export default function RequestTransportForm() {
  const formId = useId();
  const [isPending, startTransition] = useTransition();
  const [formState, setFormState] = useState<FormState>({ status: 'idle', message: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const validate = (data: FormData): FieldErrors => {
    const errors: FieldErrors = {};
    const fullName = (data.get('fullName') as string || '').trim();
    const phone = (data.get('phone') as string || '').trim();
    const pickupLocation = (data.get('pickupLocation') as string || '').trim();
    const destinationLocation = (data.get('destinationLocation') as string || '').trim();
    const transportDate = (data.get('transportDate') as string || '').trim();
    const transportTime = (data.get('transportTime') as string || '').trim();

    if (!fullName) errors.fullName = 'Full name is required.';
    else if (fullName.length < 2) errors.fullName = 'Please enter your full name.';

    if (!phone) errors.phone = 'Phone number is required.';
    else if (!isValidPhone(phone)) errors.phone = 'Enter a valid phone number (e.g. (201) 555-0100).';

    if (!pickupLocation) errors.pickupLocation = 'Pickup location is required.';
    else if (pickupLocation.length < 5) errors.pickupLocation = 'Please enter a complete pickup address.';

    if (!destinationLocation) errors.destinationLocation = 'Destination is required.';
    else if (destinationLocation.length < 5) errors.destinationLocation = 'Please enter a complete destination address.';

    if (!transportDate) errors.transportDate = 'Transport date is required.';
    else if (!isValidDate(transportDate)) errors.transportDate = 'Please select a valid date.';

    if (!transportTime) errors.transportTime = 'Transport time is required.';

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const errors = validate(data);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    startTransition(async () => {
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          body: JSON.stringify(Object.fromEntries(data)),
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          setFormState({ status: 'success', message: 'Thank you! Your request has been received. We will contact you within 1 business hour to confirm your transport.' });
          form.reset();
        } else {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || 'Server error');
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Something went wrong.';
        setFormState({ status: 'error', message: msg === 'Server error' ? 'Something went wrong. Please call us directly at (551) 326-9573.' : msg });
      }
    });
  };

  if (formState.status === 'success') {
    return (
      <div
        className="bg-sage/10 border border-sage/40 rounded-2xl p-8 text-center"
        role="alert"
        aria-live="polite"
      >
        <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={28} className="text-sage" variant="solid" />
        </div>
        <h3 className="font-display text-xl font-semibold text-primary mb-2">Request Received!</h3>
        <p className="text-muted-foreground leading-relaxed">{formState.message}</p>
        <button
          className="btn-outline-navy mt-6 text-sm"
          onClick={() => setFormState({ status: 'idle', message: '' })}
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-label="Request medical transport"
      className="space-y-6"
    >
      {formState.status === 'error' && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{formState.message}</p>
        </div>
      )}

      {/* Row 1: Name + Phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-name`} className="form-label-base">
            Full Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-name`}
            name="fullName"
            type="text"
            required
            autoComplete="name"
            placeholder="Maria Rodriguez"
            className={`form-input-base${fieldErrors.fullName ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.fullName ? `${formId}-name-err` : undefined}
            aria-invalid={!!fieldErrors.fullName}
            maxLength={100}
          />
          {fieldErrors.fullName && (
            <p id={`${formId}-name-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.fullName}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="form-label-base">
            Phone Number <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-phone`}
            name="phone"
            type="tel"
            required
            autoComplete="tel"
            placeholder="(201) 555-0100"
            className={`form-input-base${fieldErrors.phone ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.phone ? `${formId}-phone-err` : undefined}
            aria-invalid={!!fieldErrors.phone}
            maxLength={30}
          />
          {fieldErrors.phone && (
            <p id={`${formId}-phone-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.phone}</p>
          )}
        </div>
      </div>

      {/* Row 2: Pickup Location */}
      <div>
        <label htmlFor={`${formId}-pickup`} className="form-label-base">
          Pickup Location <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id={`${formId}-pickup`}
          name="pickupLocation"
          type="text"
          required
          placeholder="123 Main St, Paterson, NJ 07501"
          className={`form-input-base${fieldErrors.pickupLocation ? ' border-red-400 focus:ring-red-300' : ''}`}
          aria-required="true"
          aria-describedby={fieldErrors.pickupLocation ? `${formId}-pickup-err` : undefined}
          aria-invalid={!!fieldErrors.pickupLocation}
          maxLength={300}
        />
        {fieldErrors.pickupLocation && (
          <p id={`${formId}-pickup-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.pickupLocation}</p>
        )}
      </div>

      {/* Row 3: Destination Location */}
      <div>
        <label htmlFor={`${formId}-destination`} className="form-label-base">
          Destination Location <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id={`${formId}-destination`}
          name="destinationLocation"
          type="text"
          required
          placeholder="456 Hospital Ave, Wayne, NJ 07470"
          className={`form-input-base${fieldErrors.destinationLocation ? ' border-red-400 focus:ring-red-300' : ''}`}
          aria-required="true"
          aria-describedby={fieldErrors.destinationLocation ? `${formId}-dest-err` : undefined}
          aria-invalid={!!fieldErrors.destinationLocation}
          maxLength={300}
        />
        {fieldErrors.destinationLocation && (
          <p id={`${formId}-dest-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.destinationLocation}</p>
        )}
      </div>

      {/* Row 4: Date + Time side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-date`} className="form-label-base">
            Date of Transport <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-date`}
            name="transportDate"
            type="date"
            required
            className={`form-input-base${fieldErrors.transportDate ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.transportDate ? `${formId}-date-err` : undefined}
            aria-invalid={!!fieldErrors.transportDate}
          />
          {fieldErrors.transportDate && (
            <p id={`${formId}-date-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.transportDate}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${formId}-time`} className="form-label-base">
            Time of Transport <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id={`${formId}-time`}
            name="transportTime"
            type="time"
            required
            className={`form-input-base${fieldErrors.transportTime ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true"
            aria-describedby={fieldErrors.transportTime ? `${formId}-time-err` : undefined}
            aria-invalid={!!fieldErrors.transportTime}
          />
          {fieldErrors.transportTime && (
            <p id={`${formId}-time-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.transportTime}</p>
          )}
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isPending}
        className="btn-primary w-full justify-center py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label="Submit transport request"
      >
        {isPending ? (
          <>
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Submitting…
          </>
        ) : (
          <>
            <Icon name="PaperAirplaneIcon" size={18} />
            Request Transport
          </>
        )}
      </button>

      <p className="text-xs text-center text-muted-foreground">
        By submitting, your information is handled in accordance with our privacy policy and HIPAA guidelines.
      </p>
    </form>
  );
}